import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatError } from "../lib/api";

// ── helpers ──────────────────────────────────────────────────────────────────

function applyBranding(branding) {
  if (!branding) return;
  const root = document.documentElement;
  if (branding.menu_primary_color) root.style.setProperty("--jv-primary", branding.menu_primary_color);
  if (branding.menu_background_color) root.style.setProperty("--jv-bg", branding.menu_background_color);
  if (branding.menu_text_color) root.style.setProperty("--jv-text", branding.menu_text_color);
  if (branding.menu_card_color) root.style.setProperty("--jv-card", branding.menu_card_color);
  if (branding.menu_accent_text_color) root.style.setProperty("--jv-muted", branding.menu_accent_text_color);
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

function validatePhone(raw) {
  let p = (raw || "").replace(/\D/g, "");
  if (p.startsWith("91") && p.length === 12) p = p.slice(2);
  if (p.startsWith("0") && p.length === 11) p = p.slice(1);
  if (p.length !== 10 || !"6789".includes(p[0])) return null;
  return p;
}

// ── component ─────────────────────────────────────────────────────────────────

export default function JoinVenue() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || "unknown";

  const [loading, setLoading] = useState(true);
  const [venue, setVenue] = useState(null);   // null = loading | false = error
  const [inactive, setInactive] = useState(false);

  // form state
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [anniversary, setAnniversary] = useState("");   // AB-1 — optional, like dob
  const [waConsent, setWaConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [formError, setFormError] = useState("");
  const [existingCustomerData, setExistingCustomerData] = useState(null);
  const [newCustomerBalance, setNewCustomerBalance] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/join/${slug}`);
        applyBranding(data.branding);
        setVenue(data);
        if (!data.active) setInactive(true);
      } catch {
        setVenue(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setAlreadyRegistered(false);

    const cleanName = name.trim();
    if (!cleanName) { setFormError("Please enter your full name"); return; }

    const cleanPhone = validatePhone(phone);
    if (!cleanPhone) { setFormError("Enter a valid 10-digit Indian mobile number"); return; }

    setSubmitting(true);
    try {
      const { data: res } = await api.post("/public/register-customer", {
        venue_id: venue.venue_id,
        name: cleanName,
        phone: cleanPhone,
        dob: dob || null,
        anniversary: anniversary || null,
        source,
      });
      if (res.existing) {
        // Existing customer — show appropriate screen based on what happened
        setExistingCustomerData(res);
        setAlreadyRegistered(true);
      } else {
        setSubmitted(true);
        setNewCustomerBalance(res.points_balance || 0);
      }
    } catch (err) {
      setFormError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const primary = venue?.branding?.menu_primary_color || "#9D00FF";
  const bg = venue?.branding?.menu_background_color || "#0A0A0A";
  const text = venue?.branding?.menu_text_color || "#FFFFFF";
  const card = venue?.branding?.menu_card_color || "#1A1A1A";
  const muted = venue?.branding?.menu_accent_text_color || "#A0A0A0";
  const logoUrl = venue?.branding?.menu_logo_url;
  const bonus = venue?.welcome_bonus_points || 0;

  // ── render states ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ background: "#0A0A0A", color: "#fff", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: "0.9rem", color: "#A0A0A0" }}>Loading…</div>
      </div>
    );
  }

  if (venue === false) {
    return (
      <div style={{ background: "#0A0A0A", color: "#fff", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
        <div style={{ textAlign: "center", color: "#A0A0A0", fontSize: "0.95rem" }}>
          This link is no longer active.
        </div>
      </div>
    );
  }

  const pageStyle = { background: bg, color: text, minHeight: "100vh", fontFamily: "Montserrat, sans-serif" };
  const innerStyle = { maxWidth: 390, margin: "0 auto", padding: "2rem 1.25rem 3rem" };

  if (inactive) {
    return (
      <div style={pageStyle}>
        <div style={{ ...innerStyle, display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem", paddingTop: "4rem" }}>
          {logoUrl && <img src={logoUrl} alt="" style={{ maxHeight: 80, objectFit: "contain" }} />}
          <div style={{ fontFamily: "Lora, serif", fontWeight: 700, fontSize: "1.5rem", textAlign: "center" }}>{venue.venue_name}</div>
          <div style={{ color: muted, fontSize: "0.9rem", textAlign: "center" }}>
            This registration link is currently inactive. Please contact the venue.
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={pageStyle}>
        <div style={{ ...innerStyle, display: "flex", flexDirection: "column", alignItems: "center", gap: "1.25rem", paddingTop: "3rem", textAlign: "center" }}>
          {logoUrl && <img src={logoUrl} alt="" style={{ maxHeight: 80, objectFit: "contain" }} />}
          <div style={{ fontSize: "3.5rem", lineHeight: 1, color: primary }}>✅</div>
          <div style={{ fontFamily: "Lora, serif", fontWeight: 700, fontSize: "1.6rem" }}>
            Welcome to {venue.venue_name}! 🎉
          </div>
          {newCustomerBalance > 0 && (
            <>
              <div style={{ fontWeight: 500, fontSize: "0.95rem" }}>
                <strong>{bonus} bonus points</strong> have been added to your account.
              </div>
              <div style={{
                background: `${primary}15`,
                border: `1px solid ${primary}40`,
                borderRadius: 12,
                padding: "0.75rem 1.5rem",
                fontSize: "1.1rem",
                fontWeight: 700,
                color: primary,
              }}>
                🏆 {newCustomerBalance} pts
              </div>
            </>
          )}
          <div style={{ color: muted, fontSize: "0.88rem" }}>
            Show this to staff on your next visit to start earning rewards.
          </div>
        </div>
      </div>
    );
  }

  if (alreadyRegistered && existingCustomerData) {
    const { bonus_awarded, bonus_claimed_before, points_balance, customer_name } = existingCustomerData;
    const firstName = (customer_name || "").split(" ")[0];
    return (
      <div style={pageStyle}>
        <div style={{ ...innerStyle, display: "flex", flexDirection: "column", alignItems: "center", gap: "1.25rem", paddingTop: "3rem", textAlign: "center" }}>
          {logoUrl && <img src={logoUrl} alt="" style={{ maxHeight: 80, objectFit: "contain" }} />}

          {bonus_awarded ? (
            <>
              <div style={{ fontSize: "3rem", lineHeight: 1 }}>🎉</div>
              <div style={{ fontFamily: "Lora, serif", fontWeight: 700, fontSize: "1.4rem" }}>
                Bonus added, {firstName}!
              </div>
              <div style={{ fontWeight: 500, fontSize: "0.95rem" }}>
                <strong>{bonus} bonus points</strong> have been added to your account.
              </div>
              <div style={{
                background: `${primary}15`,
                border: `1px solid ${primary}40`,
                borderRadius: 12,
                padding: "0.75rem 1.5rem",
                fontSize: "1.1rem",
                fontWeight: 700,
                color: primary,
              }}>
                🏆 {points_balance} pts
              </div>
              <div style={{ color: muted, fontSize: "0.88rem" }}>
                Show this to staff on your next visit to redeem your points.
              </div>
            </>
          ) : bonus_claimed_before ? (
            <>
              <div style={{ fontSize: "2.5rem" }}>👋</div>
              <div style={{ fontFamily: "Lora, serif", fontWeight: 700, fontSize: "1.4rem" }}>
                Welcome back, {firstName}!
              </div>
              <div style={{ color: muted, fontSize: "0.88rem" }}>
                You've already claimed your delivery bonus.
              </div>
              <div style={{
                background: `${primary}15`,
                border: `1px solid ${primary}40`,
                borderRadius: 12,
                padding: "0.75rem 1.5rem",
                fontSize: "1.1rem",
                fontWeight: 700,
                color: primary,
              }}>
                🏆 {points_balance} pts
              </div>
              <div style={{ color: muted, fontSize: "0.88rem" }}>
                Visit us to keep earning rewards.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: "2.5rem" }}>👋</div>
              <div style={{ fontFamily: "Lora, serif", fontWeight: 700, fontSize: "1.4rem" }}>
                Welcome back, {firstName}!
              </div>
              <div style={{
                background: `${primary}15`,
                border: `1px solid ${primary}40`,
                borderRadius: 12,
                padding: "0.75rem 1.5rem",
                fontSize: "1.1rem",
                fontWeight: 700,
                color: primary,
              }}>
                🏆 {points_balance} pts
              </div>
              <div style={{ color: muted, fontSize: "0.88rem" }}>
                Visit us to keep earning rewards.
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={innerStyle}>
        {/* Logo + venue name */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
          {logoUrl && <img src={logoUrl} alt="" style={{ maxHeight: 80, objectFit: "contain" }} />}
          <div style={{ fontFamily: "Lora, serif", fontWeight: 700, fontSize: "1.8rem", textAlign: "center" }}>
            {venue.venue_name}
          </div>
        </div>

        {/* Bonus banner */}
        {bonus > 0 && (
          <div style={{
            background: `rgba(${hexToRgb(primary)}, 0.15)`,
            border: `1px solid ${primary}`,
            borderRadius: 12,
            padding: "0.75rem 1rem",
            marginBottom: "1.5rem",
            textAlign: "center",
            color: primary,
            fontWeight: 500,
            fontSize: "0.92rem",
          }}>
            🎁 Join now &amp; get {bonus} pts worth ₹{bonus} — redeem them when you dine in with us!
          </div>
        )}

        {/* Form card */}
        <div style={{ background: card, borderRadius: 16, padding: "1.5rem", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ fontFamily: "Lora, serif", fontWeight: 700, fontSize: "1.2rem", marginBottom: "0.25rem" }}>
            Join our Loyalty Programme
          </div>
          <div style={{ color: muted, fontSize: "0.83rem", marginBottom: "1.25rem" }}>
            Earn points on every visit and unlock exclusive rewards.
          </div>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {/* Name */}
            <div>
              <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: muted, display: "block", marginBottom: 6 }}>
                Full Name
              </label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Rahul Sharma"
                style={{ width: "100%", background: bg, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "0.75rem 1rem", color: text, fontSize: "0.95rem", outline: "none", boxSizing: "border-box" }}
              />
            </div>

            {/* Phone */}
            <div>
              <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: muted, display: "block", marginBottom: 6 }}>
                Phone Number
              </label>
              <div style={{ display: "flex", alignItems: "center", background: bg, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, overflow: "hidden" }}>
                <span style={{ padding: "0 0.75rem", color: muted, fontSize: "0.9rem", borderRight: "1px solid rgba(255,255,255,0.1)", whiteSpace: "nowrap" }}>+91</span>
                <input
                  required
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="98765 43210"
                  style={{ flex: 1, background: "transparent", border: "none", padding: "0.75rem 1rem", color: text, fontSize: "0.95rem", outline: "none" }}
                />
              </div>
              <p style={{ fontSize: "0.72rem", color: muted, marginTop: 5, marginBottom: 0 }}>
                📲 We'll send your points &amp; rewards via WhatsApp.
              </p>
            </div>

            {/* Date of birth (optional) */}
            <div>
              <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: muted, display: "block", marginBottom: 6 }}>
                Date of Birth (optional)
              </label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                style={{ width: "100%", background: bg, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "0.75rem 1rem", color: dob ? text : muted, fontSize: "0.95rem", outline: "none", boxSizing: "border-box", colorScheme: "dark" }}
              />
            </div>

            {/* Anniversary (optional) — AB-1, the guest fills it themselves at no cost to staff. */}
            <div>
              <label style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: muted, display: "block", marginBottom: 6 }}>
                Anniversary (optional)
              </label>
              <input
                type="date"
                value={anniversary}
                onChange={(e) => setAnniversary(e.target.value)}
                style={{ width: "100%", background: bg, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "0.75rem 1rem", color: anniversary ? text : muted, fontSize: "0.95rem", outline: "none", boxSizing: "border-box", colorScheme: "dark" }}
              />
            </div>

            {formError && (
              <div style={{ color: "#EF4444", fontSize: "0.83rem" }}>{formError}</div>
            )}

            {/* WhatsApp consent checkbox */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                required
                checked={waConsent}
                onChange={(e) => setWaConsent(e.target.checked)}
                style={{ marginTop: 2, flexShrink: 0, accentColor: primary, width: 16, height: 16 }}
              />
              <span style={{ fontSize: "0.78rem", color: muted, lineHeight: 1.5 }}>
                I agree to receive WhatsApp messages about my loyalty points, rewards, and offers from {venue.venue_name}. View{" "}
                <a href={`/terms/${slug}`} target="_blank" rel="noopener noreferrer" style={{ color: primary, textDecoration: "underline" }}>
                  T&amp;Cs
                </a>{" "}and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: primary, textDecoration: "underline" }}>
                  Privacy Policy
                </a>.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting || !waConsent}
              style={{
                background: (submitting || !waConsent) ? `rgba(${hexToRgb(primary)}, 0.5)` : primary,
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.95rem",
                border: "none",
                borderRadius: 12,
                padding: "0.875rem",
                minHeight: 48,
                cursor: (submitting || !waConsent) ? "not-allowed" : "pointer",
                width: "100%",
                transition: "opacity 0.15s",
              }}
            >
              {submitting ? "Joining…" : "Join & Claim My Points →"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
